import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Home, ShoppingBag, AlertCircle, Search } from "lucide-react";
import { ParticleBackground } from "@/components/ParticleBackground";
import { SEOHead } from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { products } from "@/data/products";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  // Get 3 popular products to recommend
  const popularProducts = products
    .filter(p => p.slug !== '/equestrian') // Exclude coming soon
    .slice(0, 3);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to shop with search would go here
      // For now, just navigate to shop
      navigate('/shop');
    }
  };

  return (
    <>
      <SEOHead
        title="Page Not Found | Samboards"
        description="The page you're looking for doesn't exist. Return to our home page or browse our shop for tactile sensory boards and products."
        canonical={`https://samboards.co.uk${location.pathname}`}
      />
      
      <ParticleBackground />
      
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          {/* Error Icon */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-400/20 blur-3xl rounded-full" />
              <AlertCircle className="relative w-24 h-24 text-emerald-400" strokeWidth={1.5} />
            </div>
          </div>

          {/* 404 Heading */}
          <h1 className="text-8xl md:text-9xl font-bold mb-6 bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 bg-clip-text text-transparent tracking-tight">
            404
          </h1>

          {/* Message */}
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-100 mb-4">
            Page not found
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-md mx-auto">
            We couldn't find the page you're looking for. It may have been moved or no longer exists.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-md mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search our products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-3 w-full bg-slate-800/50 border-slate-700 text-slate-100 placeholder:text-slate-400 focus:border-emerald-400 focus:ring-emerald-400"
              />
            </div>
          </form>

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button asChild size="lg" className="min-w-[180px]">
              <Link to="/">
                <Home className="w-5 h-5" />
                Back to home
              </Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="min-w-[180px]">
              <Link to="/shop">
                <ShoppingBag className="w-5 h-5" />
                Visit shop
              </Link>
            </Button>
          </div>

          {/* Popular Products */}
          <div className="mt-16 pt-8 border-t border-slate-700/50">
            <p className="text-sm text-slate-400 mb-6">Popular products you might like</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {popularProducts.map((product) => (
                <Link
                  key={product.slug}
                  to={`/shop`}
                  className="group bg-slate-800/50 backdrop-blur-md border border-slate-700/60 rounded-xl p-4 hover:border-emerald-400/50 transition-all hover:shadow-[0_0_20px_rgba(110,237,193,0.2)]"
                >
                  <div className="aspect-square bg-slate-900/50 rounded-lg mb-3 overflow-hidden">
                    {product.images && product.images[0] ? (
                      <img
                        src={product.images[0].src}
                        alt={product.images[0].alt}
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform"
                      />
                    ) : product.image ? (
                      <img
                        src={product.image.src}
                        alt={product.image.alt}
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-500">
                        <ShoppingBag className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-slate-100 mb-1 group-hover:text-emerald-400 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-lg font-bold text-emerald-400">
                    £{product.price.toFixed(2)}
                  </p>
                </Link>
              ))}
            </div>
          </div>

          {/* Helpful Links */}
          <div className="mt-12 pt-8 border-t border-slate-700/50">
            <p className="text-sm text-slate-400 mb-4">Quick links</p>
            <div className="flex flex-wrap gap-4 justify-center text-sm">
              <Link to="/about" className="text-emerald-400 hover:text-emerald-300 transition-colors hover:underline">
                About us
              </Link>
              <span className="text-slate-600">•</span>
              <Link to="/contact" className="text-emerald-400 hover:text-emerald-300 transition-colors hover:underline">
                Contact
              </Link>
              <span className="text-slate-600">•</span>
              <Link to="/faq" className="text-emerald-400 hover:text-emerald-300 transition-colors hover:underline">
                FAQs
              </Link>
              <span className="text-slate-600">•</span>
              <Link to="/access-to-work-kits" className="text-emerald-400 hover:text-emerald-300 transition-colors hover:underline">
                Access to Work Kits
              </Link>
              <span className="text-slate-600">•</span>
              <Link to="/feedback" className="text-emerald-400 hover:text-emerald-300 transition-colors hover:underline">
                Feedback
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotFound;
